import { Controller } from "@hotwired/stimulus"
import { writeDraft } from "../lib/maps_draft"

const STEP_START = "start"
const STEP_CHECK = "check"
const STEP_ASSETS = "assets"
const MESSAGE_LOADED = "sm:loaded"
const VIEW_2D = "2d"
const VIEW_3D = "3d"
// If a frame never says it is ready, it is swapped in anyway, so the example can never get stuck behind a frame that is not coming.
const SWAP_FALLBACK_MS = 15000
const GOOGLE_WAIT_MS = 8000
const SOURCE = "maps_flow"

// The Maps tab's first-time flow (see `maps_browse/_flow`): an example map live in 3D (a frame of the Maps editor's embeddable 3D page),
// the visitor's own site under a fixed pin, and what could be added to it. It owns the steps and the draft; nothing is saved until they
// sign up, and nothing can be placed without an account.
export default class extends Controller {
  static targets = [
    "screen", "frame", "stageLoading", "card", "info", "infoTitle", "infoLine", "infoInitial", "viewButton", "stageTypeButton", "find", "findInput", "findError",
    "map", "pin", "circle", "checkOnly", "assetsOnly", "checkSearch", "typeButton",
    "venueName", "templateChip", "assetsPanel", "assetsSub", "assetSearch", "groupButton", "assetGrid", "assetNote", "popover", "popoverTitle",
    "gate", "gateBody", "gateCreate", "gateForm", "gateKeep"
  ]
  static values = { examples: Array, embedOrigin: String, fallbackCenter: Object, siteZoom: Number, assetsUrl: String, saveUrl: String, firstGroup: String, texts: Object }

  connect() {
    this.index = 0
    this.view = VIEW_3D
    // The basemap under the example: unknown until a frame says which it opened on, then whatever the visitor last chose.
    this.stageType = null
    this.stageTypeChosen = false
    this.site = null
    this.place = null
    this.groupKey = this.firstGroupValue
    this.assets = null
    // A phone's address bar is one slim line, so it says less.
    if (window.matchMedia("(max-width: 760px)").matches) this.findInputTarget.placeholder = this.findInputTarget.dataset.shortPlaceholder
    this.fitToScreen()
    this.onResize = () => this.fitToScreen()
    window.addEventListener("resize", this.onResize)
    this.onMessage = (event) => this.receive(event)
    window.addEventListener("message", this.onMessage)
    this.setStep(STEP_START)
    this.loadExample(0)
    this.whenGoogle().then(() => this.attachFindSearch())
  }

  disconnect() {
    window.removeEventListener("message", this.onMessage)
    window.removeEventListener("resize", this.onResize)
    clearTimeout(this.swapTimer)
  }

  // ── Fitting the screen ──

  // On a phone the start screen fills what the site's own header leaves, to the bottom of the screen, with no cream left over. The header is two
  // rows whose height depends on the device (and the text size), so it is measured, not assumed.
  fitToScreen() {
    const nav = document.querySelector("nav.ge-nav")
    if (nav) this.element.style.setProperty("--mp-top", `${Math.round(nav.getBoundingClientRect().bottom)}px`)
  }

  // ── Steps ──

  setStep(step) {
    this.step = step
    this.element.dataset.step = step
    this.element.classList.toggle("mp--start", step === STEP_START)
    this.screenTargets.forEach((screen) => { screen.hidden = (step === STEP_START) !== (screen.dataset.screen === STEP_START) })
    this.checkOnlyTargets.forEach((element) => { element.hidden = step !== STEP_CHECK })
    this.assetsOnlyTargets.forEach((element) => { element.hidden = step !== STEP_ASSETS })
    this.pinTarget.hidden = step !== STEP_CHECK
    this.circleTarget.hidden = step !== STEP_CHECK
    if (step === STEP_START) this.resumeExample()
    else this.stopExample()
  }

  // ── START: an example map, live in 3D ──

  get example() {
    return this.examplesValue[this.index]
  }

  get hasExamples() {
    return this.examplesValue.length > 0
  }

  chooseExample(event) {
    this.loadExample(Number(event.currentTarget.dataset.index))
  }

  // The first example fills the first frame; every later one is loaded in the other frame behind it and swapped in once it says it is ready.
  loadExample(index) {
    if (!this.hasExamples) return

    this.index = index
    this.cardTargets.forEach((card, position) => card.setAttribute("aria-pressed", String(position === index)))
    this.infoTitleTarget.textContent = this.example.title
    this.infoLineTarget.textContent = this.example.description
    this.infoLineTarget.hidden = !this.example.description
    this.infoInitialTarget.textContent = this.example.title.slice(0, 1).toUpperCase()
    const [front, back] = this.frameTargets
    const incoming = this.showing === front ? back : front
    // The first example has the whole stage to itself; a later one loads behind the one showing, so say so.
    this.stageLoadingTarget.hidden = false
    this.stageLoadingTarget.classList.toggle("mp-stage__loading--over", !!this.showing)
    this.pending = incoming
    incoming.src = this.example.embed_url
    clearTimeout(this.swapTimer)
    this.swapTimer = setTimeout(() => this.swapIn(incoming), SWAP_FALLBACK_MS)
  }

  swapIn(incoming) {
    if (incoming !== this.pending) return

    clearTimeout(this.swapTimer)
    this.frameTargets.forEach((frame) => frame.classList.toggle("mp-stage__frame--front", frame === incoming))
    const outgoing = this.frameTargets.find((frame) => frame !== incoming)
    if (outgoing && this.showing && outgoing !== incoming) setTimeout(() => { if (outgoing !== this.showing) outgoing.removeAttribute("src") }, 700)
    this.showing = incoming
    this.pending = null
    this.stageLoadingTarget.hidden = true
  }

  receive(event) {
    if (event.origin !== this.embedOriginValue) return

    const frame = this.frameTargets.find((candidate) => candidate.contentWindow === event.source)
    if (!frame) return

    // Shown once the picture is complete (the ground under the site and the models are in), not when the viewer merely starts: before that it is a bare
    // plane with things popping in, which reads as broken.
    if (event.data.type === MESSAGE_LOADED && frame === this.pending) {
      // A new example opens in the view the visitor has chosen, not always in 3D.
      this.tellFrame(frame, this.view)
      this.followBasemap(frame, event.data.mapType)
      this.swapIn(frame)
    }
  }

  // 2D (straight down, still) or 3D (tilted, turning slowly): the page draws the switch, the Maps editor's frame moves its camera.
  setView(event) {
    this.view = event.currentTarget.dataset.view
    this.viewButtonTargets.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.view === this.view)))
    if (this.showing) this.tellFrame(this.showing, this.view)
  }

  // The visitor's basemap choice stays when they pick another example; until they choose, the page shows whichever basemap the example opened on.
  setStageMapType(event) {
    this.stageType = event.currentTarget.dataset.type
    this.stageTypeChosen = true
    this.markStageType()
    if (this.showing) this.tellFrameMapType(this.showing, this.stageType)
  }

  followBasemap(frame, openedOn) {
    if (this.stageTypeChosen && this.stageType !== openedOn) this.tellFrameMapType(frame, this.stageType)
    else if (openedOn) this.stageType = openedOn
    this.markStageType()
  }

  markStageType() {
    this.stageTypeButtonTargets.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.type === this.stageType)))
  }

  tellFrameMapType(frame, mapType) {
    frame.contentWindow?.postMessage({ type: "sm:setMapType", mapType }, this.embedOriginValue)
  }

  tellFrame(frame, view) {
    frame.contentWindow?.postMessage({ type: "sm:setView", view }, this.embedOriginValue)
  }

  // A 3D scene is not free: it is put away while another step shows, and loaded again on the way back.
  stopExample() {
    this.frameTargets.forEach((frame) => frame.removeAttribute("src"))
    this.showing = null
  }

  resumeExample() {
    if (this.hasExamples && !this.showing) {
      this.stageLoadingTarget.hidden = false
      this.loadExample(this.index)
    }
  }

  // ── START: the address box ──

  attachFindSearch() {
    if (!window.google?.maps?.places?.Autocomplete) return

    this.findAutocomplete = new google.maps.places.Autocomplete(this.findInputTarget, { fields: ["geometry", "name", "formatted_address"] })
    this.findAutocomplete.addListener("place_changed", () => {
      const place = this.findAutocomplete.getPlace()
      if (place?.geometry?.location) this.buildFromPlace(place)
    })
  }

  findVenue(event) {
    event.preventDefault()
    this.findErrorTarget.hidden = true
    const text = this.findInputTarget.value.trim()
    if (!text) return this.showFindError(this.textsValue.empty)

    this.geocode(text).then((place) => place ? this.buildFromPlace(place) : this.showFindError(this.textsValue.not_found, true))
  }

  showFindError(message, offerByHand = false) {
    this.findErrorTarget.replaceChildren(document.createTextNode(`${message} `))
    if (offerByHand) {
      const link = document.createElement("button")
      link.type = "button"
      link.textContent = this.textsValue.by_hand
      link.addEventListener("click", () => this.placeByHand())
      this.findErrorTarget.append(link)
    }
    this.findErrorTarget.hidden = false
  }

  // Not found: the map opens on a default place and they move it to their site by hand, which always works.
  placeByHand() {
    const { lat, lng, zoom } = this.fallbackCenterValue
    this.enterCheck({ lat, lng, name: "", zoom })
  }

  buildFromPlace(place) {
    const location = place.geometry.location
    this.enterCheck({ lat: location.lat(), lng: location.lng(), name: place.name || place.formatted_address || this.findInputTarget.value })
  }

  geocode(address) {
    return this.whenGoogle().then(() => new Promise((resolve) => {
      if (!window.google?.maps?.Geocoder) return resolve(null)

      new google.maps.Geocoder().geocode({ address }, (results, status) => {
        const found = status === "OK" && results?.[0]
        resolve(found ? { geometry: { location: found.geometry.location }, name: address, formatted_address: found.formatted_address } : null)
      })
    }))
  }

  // ── CHECK: is the pin on your site? ──

  enterCheck({ lat, lng, name, zoom }) {
    this.site = { lat, lng, name, zoom: zoom || this.siteZoomValue }
    this.setStep(STEP_CHECK)
    this.whenGoogle().then(() => {
      this.ensureMap()
      this.map.setCenter({ lat, lng })
      this.map.setZoom(this.site.zoom)
      this.checkSearchTarget.value = name || ""
      google.maps.event.trigger(this.map, "resize")
    })
  }

  ensureMap() {
    if (this.map) return

    this.map = new google.maps.Map(this.mapTarget, {
      center: { lat: this.site.lat, lng: this.site.lng }, zoom: this.site.zoom, mapTypeId: "satellite",
      disableDefaultUI: true, scaleControl: true, gestureHandling: "greedy", clickableIcons: false
    })
    this.map.addListener("idle", () => { if (this.step === STEP_ASSETS) this.saveDraft() })
    if (window.google.maps.places?.Autocomplete) this.attachCheckSearch()
  }

  attachCheckSearch() {
    const autocomplete = new google.maps.places.Autocomplete(this.checkSearchTarget, { fields: ["geometry", "name", "formatted_address"] })
    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace()
      if (!place?.geometry?.location) return

      this.site = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng(), name: place.name || place.formatted_address, zoom: this.siteZoomValue }
      this.map.setCenter({ lat: this.site.lat, lng: this.site.lng })
      this.map.setZoom(this.site.zoom)
    })
  }

  setMapType(event) {
    const type = event.currentTarget.dataset.type
    this.map?.setMapTypeId(type)
    this.typeButtonTargets.forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.type === type)))
  }

  zoomIn() { this.map?.setZoom(this.map.getZoom() + 1) }
  zoomOut() { this.map?.setZoom(this.map.getZoom() - 1) }

  recentre() {
    if (!this.map || !this.site) return

    this.map.setCenter({ lat: this.site.lat, lng: this.site.lng })
    this.map.setZoom(this.site.zoom)
  }

  clearSearch() {
    this.checkSearchTarget.value = ""
    this.checkSearchTarget.focus()
  }

  // "Search again" is a quick fix for a wrong place: the search box takes over, suggestions and all.
  searchAgain() {
    this.checkSearchTarget.focus()
    this.checkSearchTarget.select()
  }

  confirmSite() {
    const centre = this.map.getCenter()
    this.site = { ...this.site, lat: centre.lat(), lng: centre.lng(), zoom: this.map.getZoom() }
    this.saveDraft()
    this.enterAssets()
  }

  // ── ASSETS: what could be added ──

  enterAssets() {
    this.setStep(STEP_ASSETS)
    this.venueNameTarget.textContent = this.site.name || this.textsValue.venue_fallback
    this.templateChipTarget.textContent = this.example ? this.textsValue.template_chip.replace("{name}", this.example.title) : ""
    this.templateChipTarget.hidden = !this.example
    this.marker?.setMap(null)
    this.marker = new google.maps.Marker({ position: { lat: this.site.lat, lng: this.site.lng }, map: this.map })
    google.maps.event.trigger(this.map, "resize")
    this.map.setCenter({ lat: this.site.lat, lng: this.site.lng })
    this.loadAssets()
  }

  backToCheck() {
    this.marker?.setMap(null)
    this.setStep(STEP_CHECK)
    google.maps.event.trigger(this.map, "resize")
  }

  loadAssets() {
    if (this.assets) return this.renderAssets()

    fetch(this.assetsUrlValue, { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : []))
      .catch(() => [])
      .then((groups) => { this.assets = groups; this.renderAssets() })
  }

  chooseGroup(event) {
    this.groupKey = event.currentTarget.dataset.group
    this.assetSearchTarget.value = ""
    this.renderAssets()
  }

  searchAssets() {
    this.renderAssets()
  }

  // One group at a time, or, while something is typed, every group's matches.
  renderAssets() {
    const query = this.assetSearchTarget.value.trim().toLowerCase()
    this.groupButtonTargets.forEach((button) => button.setAttribute("aria-pressed", String(!query && button.dataset.group === this.groupKey)))
    const groups = this.assets || []
    const items = query
      ? groups.flatMap((group) => group.items).filter((item) => item.name.toLowerCase().includes(query))
      : (groups.find((group) => group.key === this.groupKey)?.items || [])
    this.assetGridTarget.replaceChildren(...items.map((item) => this.tile(item)))
    const nothing = groups.length === 0 || groups.every((group) => group.items.length === 0)
    this.assetNoteTarget.hidden = !nothing && items.length > 0
    this.assetNoteTarget.textContent = nothing ? this.textsValue.library_down : this.textsValue.none_found
  }

  tile(item) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "mp-tile"
    const picture = document.createElement("img")
    picture.alt = ""
    picture.src = `data:image/svg+xml;utf8,${encodeURIComponent(item.svg)}`
    const name = document.createElement("span")
    name.textContent = item.name
    button.append(picture, name)
    button.addEventListener("click", () => this.tapAsset(button, item))
    return button
  }

  // Tapping an asset says what it would do, and offers the account that does it.
  tapAsset(button, item) {
    this.assetGridTarget.querySelectorAll(".mp-tile").forEach((tile) => tile.classList.toggle("mp-tile--picked", tile === button))
    this.popoverTitleTarget.textContent = this.textsValue.popover_title.replace("{name}", item.name)
    this.popoverTarget.hidden = false
  }

  // ── The draft and the sign-up ──

  draft() {
    return { lat: this.site.lat, lng: this.site.lng, zoom: this.site.zoom, name: this.site.name || "", templateGuid: this.example?.guid || "", templateName: this.example?.title || "" }
  }

  saveDraft() {
    const centre = this.step === STEP_ASSETS && this.map ? this.map.getCenter() : null
    if (centre) this.site = { ...this.site, zoom: this.map.getZoom() }
    writeDraft(this.draft())
  }

  // Where the draft goes on the server, so that the link in their email (or Google) lands them in their own site.
  async keepDraftForSignUp() {
    const draft = this.draft()
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({ lat: draft.lat, lng: draft.lng, zoom: draft.zoom, template_guid: draft.templateGuid, template_name: draft.templateName, source: SOURCE })
      })
    } catch {
      // The draft is still in this browser; the account can be made without it being carried over.
    }
  }

  async openGate() {
    if (!this.site) return

    await this.keepDraftForSignUp()
    this.popoverTarget.hidden = true
    this.gateBodyTarget.textContent = this.textsValue.gate_body.replace("{venue}", this.site.name || this.textsValue.venue_fallback)
    this.gateFormTarget.hidden = true
    this.gateCreateTarget.hidden = false
    this.openedFrom = document.activeElement
    this.gateTarget.hidden = false
    this.gateCreateTarget.focus()
  }

  closeGate() {
    this.gateTarget.hidden = true
    this.openedFrom?.focus?.()
  }

  showGateForm() {
    this.gateCreateTarget.hidden = true
    this.gateFormTarget.hidden = false
    this.gateFormTarget.querySelector("input[type=email]")?.focus()
  }

  gateKeydown(event) {
    if (event.key === "Escape") return this.closeGate()
    if (event.key !== "Tab") return

    const focusable = [...this.gateTarget.querySelectorAll("button, input, a[href]")].filter((element) => element.offsetParent !== null)
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
  }

  // ── Google Maps ──

  // Resolves once Google Maps is on the page (the home page loads it and says so), or after a wait with whatever is there (the home page
  // supplies a stand-in with no key), so a missing map shows a plain message instead of a spinner that never ends.
  whenGoogle() {
    if (window.realGoogleMapsLoaded || window.google?.maps?.Geocoder) return Promise.resolve()

    return new Promise((resolve) => {
      const done = () => { document.removeEventListener("google:maps:ready", done); clearTimeout(timer); resolve() }
      const timer = setTimeout(done, GOOGLE_WAIT_MS)
      document.addEventListener("google:maps:ready", done)
    })
  }
}
